import React from 'react';
import { Icon } from '@iconify/react';
import { resolveFourStats } from '@/components/client/mobile/RaporEventCard';

export interface SorotanCardProps {
  eventName: string;
  matches: number;
  goals: number;
  assists: number;
  winRate: number;
  point?: number;
  pointLoss?: number;
  isRacket?: boolean;
  selectedStatKeys?: string[];
  rawStats?: any;
  bgImage?: string;
  transparent?: boolean;
  className?: string;
}

const DEFAULT_BG_IMAGE = 'https://cdn.sporto.id/bgshare.webp';

export const SorotanCard: React.FC<SorotanCardProps> = ({
  eventName,
  matches,
  goals,
  assists,
  winRate,
  point,
  pointLoss,
  isRacket = false,
  selectedStatKeys,
  rawStats,
  bgImage,
  transparent = true,
  className = '',
}) => {
  const activeBgImage = bgImage || DEFAULT_BG_IMAGE;

  const statItems = resolveFourStats(
    selectedStatKeys,
    rawStats || { goals, assists, matches, win_rate: winRate, point, point_loss: pointLoss },
    isRacket
  );

  return (
    <div
      className={`relative w-[540px] h-[960px] overflow-hidden select-none ${
        transparent ? 'bg-transparent shadow-none' : 'bg-[#0E1726] shadow-[0_8px_30px_rgba(14,23,38,0.18)]'
      } ${className}`}
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      {/* Optional Background Image & Shadow Gradient Overlay (only if not transparent) */}
      {!transparent && (
        <>
          <img
            src={activeBgImage}
            alt="Foto pertandingan"
            className="absolute inset-0 w-full h-full object-cover object-[58%_34%]"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(14, 23, 38, 0.35) 0%, rgba(14, 23, 38, 0.15) 32%, rgba(14, 23, 38, 0.55) 62%, rgba(14, 23, 38, 0.95) 100%)',
            }}
          />
        </>
      )}

      {/* Content Container */}
      <div className="absolute inset-0 flex flex-col items-center justify-start pt-[52px] px-[40px] pb-[44px]">
        {/* Header Location / Event Name */}
        <div className="flex flex-col items-center gap-[14px]">
          <div className="flex items-center gap-[7px]">
            <Icon
              icon="ph:map-pin"
              className="text-white/90 text-[17px]"
              style={transparent ? undefined : { filter: 'drop-shadow(0 1px 8px rgba(14,23,38,.6))' }}
            />
            <div
              className="text-[17px] font-semibold text-white truncate max-w-[420px]"
              style={transparent ? undefined : { textShadow: '0 1px 10px rgba(14,23,38,.6)' }}
            >
              {eventName}
            </div>
          </div>
        </div>

        {/* Stacked Stats */}
        <div className="flex flex-col items-center gap-[16px] mt-[28px]">
          {statItems.map((st: { key: string; label: string; icon: string; iconBg: string; iconColor: string; value: string | number }) => (
            <div key={st.key} className="flex flex-col items-center gap-[2px]">
              <div
                className="text-[13px] font-semibold text-white/80 uppercase"
                style={transparent ? undefined : { textShadow: '0 1px 8px rgba(14,23,38,.6)' }}
              >
                {st.label}
              </div>
              <div
                className="text-[38px] font-extrabold text-white leading-[42px] tracking-[-0.02em]"
                style={transparent ? undefined : { textShadow: '0 2px 16px rgba(14,23,38,.55)' }}
              >
                {st.value}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Logo */}
        <img
          src="https://cdn.sporto.id/sporto.svg"
          alt="Sporto"
          className="h-[24px] w-auto object-contain block mt-[32px]"
          style={transparent ? undefined : { filter: 'drop-shadow(0 2px 10px rgba(14,23,38,.5))' }}
        />
      </div>
    </div>
  );
};

export default SorotanCard;
